import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../lib/data";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";
import { createPortal } from "react-dom";
import RegionSelector from "./RegionSelector";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800/60 py-3"
          : "bg-transparent py-5"
      }`}
      data-testid="site-navbar"
    >
      <div className="container-page flex items-center justify-between">
        <Link to="/" aria-label="SolkSites home" data-testid="nav-logo-link">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  isActive
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <RegionSelector className="hidden md:block" />
          <ThemeToggle className="hidden md:inline-flex" />
          <Link
            to="/pricing"
            className="btn-primary hidden md:inline-flex"
            data-testid="nav-book-call-button"
          >
            Get your quote
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            aria-label="Open menu"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
            onClick={() => setOpen(true)}
            data-testid="nav-mobile-open"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>

      {open && createPortal(
        <div
          className="lg:hidden fixed inset-0 z-50 bg-white dark:bg-slate-950"
          role="dialog"
          aria-modal="true"
          data-testid="mobile-nav-drawer"
        >
          <div className="container-page pt-5 flex items-center justify-between">
            <Logo />
            <RegionSelector></RegionSelector>
            <button
              type="button"
              aria-label="Close menu"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-slate-200 dark:border-slate-700"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="container-page mt-10 flex flex-col gap-2">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-3xl font-semibold tracking-tight py-3 border-b border-slate-100 dark:border-slate-800 ${
                    isActive ? "text-blue-600" : "text-slate-900 dark:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/pricing"
              className="btn-primary mt-6 w-full justify-center"
              data-testid="mobile-nav-book-call"
            >
              Get your quote
              <ArrowUpRight className="h-4 w-4" />
            </Link>
            <div className="mt-6 flex items-center">
              <ThemeToggle />
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Navbar;
